import React, { useMemo, useState } from "react";

import {
  CalendarOutlined,
  CloseOutlined,
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Select, Button } from "antd";
import moment from "moment";

import DatePicker from "../../Common/DatePicker/DatePicker";
import {
  getSearchableSelectProps,
  renderSelectPopupScrollbar,
  selectPopupClassNames,
} from "../../Common/UIComponents/SelectPopupScrollbar";
import PickupFilterModal from "./PickupFilterModal";
import SupplyPickupSearchInput from "./SupplyPickupSearchInput";

import { ReactComponent as SoundModuleLineIcon } from "../../../assets/icons/Supply & Pickup/sound-module-line.svg";
import { ReactComponent as TruckIcon } from "../../../assets/icons/Supply & Pickup/truck.svg";

import "./SearchAndCreate.css";

const EMPTY_FILTERS = {
  orderDate: "",
  pickupDate: "",
  orderId: "",
  requestedBy: "",
};

const formatChipDate = (value) => {
  if (!value) return "";
  const parsed = moment(value, ["YYYY-MM-DD", "MM/DD/YYYY"], true);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : value;
};

export default function SearchAndCreate({
  createBtnLabel,
  btnIcon,
  btnClassName = "",
  btnOnClick,
  btnLoading = false,
  createDisabled = false,
  secondaryBtnLabel,
  secondaryBtnIcon,
  secondaryBtnClassName = "",
  secondaryBtnOnClick,
  secondaryBtnLoading = false,
  handleSearch: parentHandleSearch,
  search,
  setSearch,
  dates,
  setDates,
  getReportDates,
  type = "supply", // 'pickup' or 'delivery'
  pickupCategories = [],
  selectedPickupCategory = "",
  handleDeliveryPartnerChange = () => {},
  supplyRequestTypeOptions = [],
  supplyRequestTypeFilter = "",
  handleSupplyRequestTypeFilterChange = () => {},
  filters = EMPTY_FILTERS,
  onApplyFilters = () => {},
  onClearAllFilters = () => {},
  requestedByOptions = [],
}) {
  const [dateSelected, setDateSelected] = useState("");
  const [prevDateSelected, setPrevDateSelected] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);

  const handleSearch = (value) => {
    setSearch(value);
    const { date_from, date_to } = dates;
    parentHandleSearch({
      search: value,
      date_from,
      date_to,
      requestType: supplyRequestTypeFilter,
    });
  };

  const onDateSelected = (date_from, date_to) => {
    const resolvedDateTo = date_to || date_from;
    setDates({
      date_from,
      date_to: resolvedDateTo,
    });
    parentHandleSearch({
      search,
      date_from,
      date_to: resolvedDateTo,
      requestType: supplyRequestTypeFilter,
    });
  };

  const isPickup = type === "pickup";

  const deliveryPartnerLabel = useMemo(() => {
    if (!selectedPickupCategory) return "";
    const match = pickupCategories.find((opt) => opt.value === selectedPickupCategory);
    return match?.label || selectedPickupCategory;
  }, [pickupCategories, selectedPickupCategory]);

  const orderDateFilter = filters?.orderDate || "";
  const pickupDateFilter = filters?.pickupDate || "";
  const orderIdFilter = filters?.orderId || "";
  const requestedByFilter = filters?.requestedBy || "";

  const appliedFilterChips = useMemo(() => {
    if (!isPickup) return [];
    const chips = [];

    if (selectedPickupCategory && deliveryPartnerLabel) {
      chips.push({
        key: "deliveryPartner",
        label: "Delivery Partner",
        value: deliveryPartnerLabel,
        icon: <TruckIcon className="SearchAndCreate__AppliedFilterIcon" aria-hidden />,
      });
    }
    if (orderDateFilter) {
      chips.push({
        key: "orderDate",
        label: "Order Date",
        value: formatChipDate(orderDateFilter),
        icon: <CalendarOutlined className="SearchAndCreate__AppliedFilterIcon" aria-hidden />,
      });
    }
    if (pickupDateFilter) {
      chips.push({
        key: "pickupDate",
        label: "Pickup Date",
        value: formatChipDate(pickupDateFilter),
        icon: <CalendarOutlined className="SearchAndCreate__AppliedFilterIcon" aria-hidden />,
      });
    }
    if (orderIdFilter) {
      chips.push({
        key: "orderId",
        label: "Order ID",
        value: orderIdFilter,
        icon: <SearchOutlined className="SearchAndCreate__AppliedFilterIcon" aria-hidden />,
      });
    }
    if (requestedByFilter) {
      chips.push({
        key: "requestedBy",
        label: "Requested by",
        value: requestedByFilter,
        icon: <UserOutlined className="SearchAndCreate__AppliedFilterIcon" aria-hidden />,
      });
    }

    return chips;
  }, [
    isPickup,
    selectedPickupCategory,
    deliveryPartnerLabel,
    orderDateFilter,
    pickupDateFilter,
    orderIdFilter,
    requestedByFilter,
  ]);

  const hasActiveFilters = appliedFilterChips.length > 0;

  const handleApplyFilters = (nextFilters) => {
    onApplyFilters(nextFilters);
    setShowFilterModal(false);
  };

  const handleRemoveChip = (chipKey) => {
    if (chipKey === "deliveryPartner") {
      handleDeliveryPartnerChange(undefined);
      return;
    }

    const nextFilters = {
      orderDate: filters?.orderDate || "",
      pickupDate: filters?.pickupDate || "",
      orderId: filters?.orderId || "",
      requestedBy: filters?.requestedBy || "",
      [chipKey]: "",
    };
    onApplyFilters(nextFilters);
  };

  const handleClearAll = () => {
    onClearAllFilters();
  };

  return (
    <div className={`SearchAndCreate${isPickup ? " SearchAndCreate--pickup" : ""}`}>
      <div className="SearchAndCreate__Select">
        <div className="SearchAndCreate__SearchGroup">
          <SupplyPickupSearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              if (!value) handleSearch("");
            }}
            onSearch={handleSearch}
            placeholder={isPickup ? "Search anything..." : "Search by Order ID"}
          />
          {isPickup ? (
            <button
              type="button"
              className={`SearchAndCreate__FilterToggle${showFilterModal || hasActiveFilters ? " SearchAndCreate__FilterToggle--active" : ""}${hasActiveFilters ? " SearchAndCreate__FilterToggle--hasFilters" : ""}`}
              aria-label="Open filters"
              aria-pressed={showFilterModal}
              onClick={() => setShowFilterModal(true)}
            >
              <SoundModuleLineIcon aria-hidden />
            </button>
          ) : (
            <>
              <div className="SearchAndCreate__Spacer" />
              <div className="SearchAndCreate__DatePicker">
                <DatePicker
                  handleSearchDate={onDateSelected}
                  getReportDates={getReportDates}
                  value={dateSelected}
                  onChange={setDateSelected}
                  prevValue={prevDateSelected}
                  setPrevValue={setPrevDateSelected}
                  searchOnClear
                  datePickerDisplayProps={{
                    iconTooltipTitle: "Sort by date",
                  }}
                />
              </div>
              <div className="SearchAndCreate__Spacer" />
              <Select
                allowClear
                options={supplyRequestTypeOptions}
                placeholder="Requests Type"
                className="SearchAndCreate__RequestTypeFilter"
                {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
                classNames={selectPopupClassNames()}
                virtual={false}
                listHeight={256}
                popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                  optionCount: (supplyRequestTypeOptions || []).length,
                })}
                value={supplyRequestTypeFilter || undefined}
                onChange={handleSupplyRequestTypeFilterChange}
              />
            </>
          )}
        </div>
        {isPickup && (
          <Select
            allowClear
            options={pickupCategories}
            placeholder="Delivery Partner"
            className="SearchAndCreate__DeliveryFilter"
            {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
            classNames={selectPopupClassNames()}
            virtual={false}
            listHeight={256}
            popupRender={(menu) => renderSelectPopupScrollbar(menu, {
              optionCount: (pickupCategories || []).length,
            })}
            value={selectedPickupCategory || undefined}
            onChange={handleDeliveryPartnerChange}
          />
        )}
        {isPickup && hasActiveFilters && (
          <div className="SearchAndCreate__AppliedFilters">
            {appliedFilterChips.map((chip) => (
              <span key={chip.key} className="SearchAndCreate__AppliedFilterChip">
                {chip.icon}
                <span className="SearchAndCreate__AppliedFilterText">
                  {`${chip.label} : ${chip.value}`}
                </span>
                <button
                  type="button"
                  className="SearchAndCreate__AppliedFilterRemove"
                  aria-label={`Remove ${chip.label} filter`}
                  onClick={() => handleRemoveChip(chip.key)}
                >
                  <CloseOutlined aria-hidden />
                </button>
              </span>
            ))}
            <button
              type="button"
              className="SearchAndCreate__ClearAllFilters"
              onClick={handleClearAll}
            >
              Clear all
            </button>
          </div>
        )}
      </div>
      <div className="SearchAndCreate__BtnContainer">
        {secondaryBtnLabel && (
          <Button
            type="default"
            className={secondaryBtnClassName}
            loading={secondaryBtnLoading}
            disabled={secondaryBtnLoading}
            onClick={secondaryBtnOnClick}
          >
            {secondaryBtnIcon}
            {secondaryBtnLabel}
          </Button>
        )}
        <Button
          type="primary"
          className={`${btnClassName}${isPickup ? " SearchAndCreate__CreateBtn--pickup" : ""}`}
          onClick={createDisabled ? undefined : btnOnClick}
          loading={btnLoading}
          disabled={btnLoading || createDisabled}
          icon={isPickup ? <PlusOutlined /> : undefined}
        >
          {!isPickup && btnIcon}
          {createBtnLabel}
        </Button>
      </div>
      {isPickup && (
        <PickupFilterModal
          open={showFilterModal}
          onCancel={() => setShowFilterModal(false)}
          onApply={handleApplyFilters}
          filters={filters}
          requestedByOptions={requestedByOptions}
          getReportDates={getReportDates}
        />
      )}
    </div>
  );
}
